import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ShieldCheck, Loader2, CheckCircle2, XCircle, ArrowLeft, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { vhrpImages } from "@/lib/site";
import { Button } from "@/components/ui/button";

function loadRazorpay() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Payment() {
  const [params] = useSearchParams();
  const tier = Number(params.get("tier")) || 21;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();

  const [order, setOrder] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | processing | success | fail
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const o = await api("/payments/order", { method: "POST", body: JSON.stringify({ tier }) });
        setOrder(o);
        setStatus("ready");
      } catch (e) {
        setError(e.message || "");
        setStatus("fail");
      }
    })();
  }, [tier]);

  const verify = useCallback(async (resp) => {
    setStatus("processing");
    try {
      await api("/payments/verify", {
        method: "POST",
        body: JSON.stringify({
          razorpay_payment_id: resp.razorpay_payment_id,
          razorpay_order_id: resp.razorpay_order_id || order.order_id,
          razorpay_signature: resp.razorpay_signature,
        }),
      });
      setStatus("success");
      toast.success(t("payOkTitle"));
    } catch (e) {
      setError(e.message || "");
      setStatus("fail");
    }
  }, [order, t]);

  const pay = useCallback(async () => {
    if (!order) return;
    if (order.test_mode) {
      // Demo mode — simulate a successful payment
      await verify({
        razorpay_payment_id: `pay_demo_${Date.now()}`,
        razorpay_order_id: order.order_id,
        razorpay_signature: "demo_signature",
      });
      return;
    }
    const ok = await loadRazorpay();
    if (!ok) {
      toast.error("Could not load Razorpay");
      return;
    }
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name: t("orgName"),
      description: `₹${tier} ${t("perYear")}`,
      image: vhrpImages.logoMark,
      prefill: { name: user?.name || "", email: user?.email || "" },
      theme: { color: "#E65100" },
      handler: (resp) => verify(resp),
      modal: { ondismiss: () => toast.message(t("payBack")) },
    });
    rzp.on("payment.failed", (r) => {
      setError(r?.error?.description || "");
      setStatus("fail");
    });
    rzp.open();
  }, [order, tier, user, verify, t]);

  return (
    <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-md flex-col items-center justify-center px-4 py-12">
      <div className="w-full rounded-3xl border border-line bg-white p-8 text-center shadow-sm">
        <img src={vhrpImages.logoMark} alt="" className="mx-auto h-16 w-16 rounded-full ring-2 ring-brand-tint" />

        {status === "loading" && (
          <div className="mt-6 flex flex-col items-center gap-3">
            <Loader2 className="h-7 w-7 animate-spin text-brand" />
            <p className="text-sm text-ink-muted">{t("payCreating")}</p>
          </div>
        )}

        {(status === "ready" || status === "processing") && (
          <>
            <h1 className="mt-5 text-xl font-black text-ink">{t("payTitle")}</h1>
            <div className="mt-4 rounded-2xl bg-brand-tint p-5">
              <div className="text-xs font-bold uppercase tracking-wide text-brand">{t("payAmount")}</div>
              <div className="mt-1 text-4xl font-black text-brand-dark">₹{tier}</div>
              {order?.test_mode && (
                <div className="mt-2 text-xs font-semibold text-ink-muted">{t("payDemo")}</div>
              )}
            </div>
            <Button
              data-testid="pay-now-button"
              onClick={pay}
              disabled={status === "processing"}
              className="mt-6 h-12 w-full rounded-full bg-brand text-base font-bold hover:bg-brand-dark"
            >
              {status === "processing" ? (
                <Loader2 className="animate-spin" />
              ) : (
                <><CreditCard size={18} className="mr-2" /> {order?.test_mode ? t("paySimulate") : `${t("payNow")} ₹${tier}`}</>
              )}
            </Button>
            <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-ink-muted">
              <ShieldCheck size={14} className="text-[#2E7D32]" /> {t("paySecured")}
            </p>
            <p className="mt-2 text-[11px] leading-snug text-ink-muted">{t("payNonRefundable")}</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle2 className="mx-auto mt-6 h-16 w-16 text-[#2E7D32]" />
            <h1 className="mt-4 text-xl font-black text-ink">{t("payOkTitle")}</h1>
            <p className="mt-2 text-sm text-ink-muted">{t("payOkSub")}</p>
            <Button data-testid="view-card-button" onClick={() => navigate("/card")} className="mt-6 h-12 w-full rounded-full bg-brand font-bold hover:bg-brand-dark">
              {t("payViewCard")}
            </Button>
          </>
        )}

        {status === "fail" && (
          <>
            <XCircle className="mx-auto mt-6 h-16 w-16 text-[#C62828]" />
            <h1 className="mt-4 text-xl font-black text-ink">{t("payFailTitle")}</h1>
            <p className="mt-2 text-sm text-ink-muted">{error || t("payFailSub")}</p>
            <Button data-testid="pay-back-button" onClick={() => navigate("/membership")} variant="outline" className="mt-6 h-12 w-full rounded-full font-bold">
              <ArrowLeft size={16} className="mr-1" /> {t("payBack")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
